import axios from "axios";
import { onMounted, onUnmounted, getCurrentInstance } from 'vue';
import { useGetters } from "./getters";

// Global flag to prevent duplicate API calls
let pwaInstallSaved = false;

/**
 * Composable for handling PWA installation tracking using Composition API
 * @returns {Object} PWA installation tracking methods
 */
export function useInstallPwa() {
    const instance = getCurrentInstance();
    const gtag = instance?.appContext.config.globalProperties.$gtag;
    const { isLoggedIn } = useGetters();

    /**
     * Handle PWA installation event
     */
    const handleInstalled = async () => {
        // Prevent duplicate calls
        if (pwaInstallSaved) {
            console.log('[PWA] Installation already tracked, skipping duplicate call');
            return;
        }

        if (isLoggedIn.value) {
            pwaInstallSaved = true; // Set flag before API call
            try {
                const response = await axios.get(
                    process.env.VUE_APP_API_BASE + "auth/saveInstalledPwa",
                    {
                        headers: {
                            "Content-type": "application/json;multipart/form-data",
                            Authorization: "Bearer " + localStorage.getItem("token"),
                        },
                    }
                );

                if (response.data.device && gtag) {
                    const deviceInfo = {
                        device_type: response.data.device.type,
                        device_browser: response.data.device.browser,
                        operating_system: response.data.device.os,
                    };
                    gtag.event("app_installed", deviceInfo);
                }
            } catch (err) {
                console.log('An error occurred while saving the installed PWA. ', err);
                pwaInstallSaved = false; // Reset on error to allow retry
            }
        }
    };

    /**
     * Setup event listener for PWA installation
     */
    const setupInstallListener = () => {
        window.addEventListener('appinstalled', handleInstalled);
    };

    /**
     * Cleanup event listener
     */
    const cleanupInstallListener = () => {
        window.removeEventListener('appinstalled', handleInstalled);
    };

    // Auto-setup on mount and cleanup on unmount
    onMounted(() => {
        setupInstallListener();
    });

    onUnmounted(() => {
        cleanupInstallListener();
    });

    return {
        handleInstalled,
        setupInstallListener,
        cleanupInstallListener
    };
}

// Legacy mixin export for backward compatibility
export default {
    computed: {
        isLoggedIn() {
            return this.$store.getters.isLoggedIn;
        }
    },
    created() {
        this._handlePwaInstall = () => {
            // Prevent duplicate calls using global flag
            if (pwaInstallSaved) {
                console.log('[PWA] Installation already tracked, skipping duplicate call');
                return;
            }

            if (this.isLoggedIn) {
                pwaInstallSaved = true; // Set flag before API call
                axios
                    .get(process.env.VUE_APP_API_BASE + "auth/saveInstalledPwa", {
                        headers: {
                            "Content-type": "application/json;multipart/form-data",
                            Authorization: "Bearer " + localStorage.getItem("token"),
                        },
                    }).then((response) => {
                        if (response.data.device) {
                            let deviceInfo = {
                                device_type: response.data.device.type,
                                device_browser: response.data.device.browser,
                                operating_system: response.data.device.os,
                            };
                            this.$gtag.event("app_installed", deviceInfo);
                        }
                    }).catch((err) => {
                        console.log('An error occurred while saving the installed PWA. ', err);
                        pwaInstallSaved = false; // Reset on error to allow retry
                    });
            }
        };
        window.addEventListener('appinstalled', this._handlePwaInstall);
    },
    beforeUnmount() {
        if (this._handlePwaInstall) {
            window.removeEventListener('appinstalled', this._handlePwaInstall);
        }
    },
    methods: {
        installed() {
            // This method exists for backward compatibility but does nothing
            // The event listener handles everything automatically
        },
    }
};

